import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext();

const DENSITY_LEVELS = ['compact', 'comfortable', 'spacious'];
const COLOR_THEMES = ['dark', 'light'];
const THEME_STORAGE_KEY = 'ncad-theme';

/**
 * Get initial density from localStorage or default to comfortable (M).
 * 'comfortable' is the current baseline visual, so the default is unchanged.
 */
function getInitialDensity() {
  const stored = localStorage.getItem('ncad-density');
  if (DENSITY_LEVELS.includes(stored)) return stored;
  return 'comfortable';
}

/**
 * Get the initial colour theme.
 *
 * Kiln dark is the product default: we deliberately do NOT read
 * prefers-color-scheme. Light is an explicit opt-in that persists.
 */
function getInitialTheme() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (COLOR_THEMES.includes(stored)) return stored;
  } catch {
    // Storage blocked (private mode, embedded webview) — fall back to dark.
  }
  return 'dark';
}

/**
 * Theme Provider - Portal Theme Management
 *
 * Manages portal-specific themes via CSS custom properties.
 * All portals use their designated theme colors.
 * Density toggle (compact/spacious) for layout preferences.
 * Colour theme: Kiln dark (default) or Kiln light, persisted.
 */
export function ThemeProvider({ children }) {
  // Portal type detection (set by portal layouts)
  const [portalType, setPortalType] = useState(null);

  // Experimental mode flag for progressive rollout
  const [experimentalMode, setExperimentalMode] = useState(true);

  // Density: 'compact' (S) | 'comfortable' (M) | 'spacious' (L)
  const [densityState, setDensityState] = useState(getInitialDensity);

  // Colour theme: 'dark' (Kiln default) | 'light' (chalk)
  const [themeState, setThemeState] = useState(getInitialTheme);

  // Apply portal attributes to document
  useEffect(() => {
    const root = document.documentElement;

    // Set experimental mode attribute
    root.setAttribute('data-experimental', experimentalMode.toString());

    // Set portal type for CSS targeting
    if (portalType) {
      root.setAttribute('data-portal', portalType);
    }

    // Set density attribute for CSS targeting
    root.setAttribute('data-density', densityState);
  }, [experimentalMode, portalType, densityState]);

  // Apply the colour theme to <html>. Dark is the default, so it carries no
  // attribute at all; only light stamps data-theme="light".
  useEffect(() => {
    const root = document.documentElement;
    if (themeState === 'light') {
      root.setAttribute('data-theme', 'light');
    } else {
      root.removeAttribute('data-theme');
    }
  }, [themeState]);

  // Register portal type (called by portal layouts)
  const registerPortal = useCallback((type) => {
    setPortalType(type);
  }, []);

  // Unregister portal (called on unmount)
  const unregisterPortal = useCallback(() => {
    setPortalType(null);
  }, []);

  // Set density to an explicit level (S/M/L control), persisted.
  const setDensity = useCallback((value) => {
    if (!DENSITY_LEVELS.includes(value)) return;
    setDensityState(value);
    localStorage.setItem('ncad-density', value);
  }, []);

  // Cycle density S → M → L → S (header icon button).
  const toggleDensity = useCallback(() => {
    setDensityState((prev) => {
      const idx = DENSITY_LEVELS.indexOf(prev);
      const next = DENSITY_LEVELS[(idx + 1) % DENSITY_LEVELS.length];
      localStorage.setItem('ncad-density', next);
      return next;
    });
  }, []);

  // Set the colour theme explicitly, persisted.
  const setTheme = useCallback((value) => {
    if (!COLOR_THEMES.includes(value)) return;
    setThemeState(value);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, value);
    } catch {
      // Storage blocked — the choice still applies for this session.
    }
  }, []);

  // Flip dark ↔ light (header sun/moon control).
  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Storage blocked — the choice still applies for this session.
      }
      return next;
    });
  }, []);

  const value = {
    // Portal management
    portalType,
    registerPortal,
    unregisterPortal,

    // Experimental features
    experimentalMode,
    setExperimentalMode,

    // Density (compact/comfortable/spacious)
    density: densityState,
    setDensity,
    toggleDensity,

    // Colour theme (dark/light)
    theme: themeState,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Hook to access theme context
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Hook for portal registration
 * Call this in your portal layout component
 *
 * @param {string} portalType - 'student' | 'staff' | 'dept-admin' | 'master-admin'
 */
export function usePortalTheme(portalType) {
  const { registerPortal, unregisterPortal, ...themeContext } = useTheme();

  useEffect(() => {
    registerPortal(portalType);
    return () => unregisterPortal();
  }, [portalType, registerPortal, unregisterPortal]);

  return themeContext;
}

export default ThemeContext;
